// Writing the floor on a price tag where a customer cannot read it.
//
// A keyword of ten distinct letters stands for the digits, in the traditional
// shop form: the first letter is 1, the ninth is 9, the tenth is 0.
//
//     S W A Y M B H U N T
//     1 2 3 4 5 6 7 8 9 0
//
// So a floor of 110 is written SST. The customer sees three letters; staff read
// the lowest price they may accept.
//
// This is obfuscation, not security. A regular customer sees hundreds of tags
// over months and can work it out with a notebook, which is why the tag carries
// the FLOOR and never the cost -- when it is eventually cracked, what leaks is
// the lowest acceptable price, which still contains the shop's margin. The cost
// stays in the app.

export const CIPHER_LENGTH = 10;

/** Digits in keyword order: position 1..9 then 0, as shops write it. */
const DIGIT_ORDER = "1234567890";

/**
 * Why a keyword is not usable, or null if it is.
 *
 * Ten DISTINCT letters: two letters standing for the same digit cannot be
 * decoded, and the shopkeeper would not find out until a tag was ambiguous.
 */
export function cipherKeywordError(keyword) {
  const letters = (keyword || "").toUpperCase().replace(/[^A-Z]/g, "");
  if (!letters) return "Enter a word with 10 different letters";
  if (letters.length !== CIPHER_LENGTH) {
    return `Needs exactly 10 letters — this has ${letters.length}`;
  }
  const repeated = [...letters].filter((c, i) => letters.indexOf(c) !== i);
  if (repeated.length) {
    return `Every letter must be different — ${[...new Set(repeated)].join(", ")} repeats`;
  }
  return null;
}

/** The digit-to-letter table, for showing staff what the keyword means. */
export function cipherTable(keyword) {
  const letters = (keyword || "").toUpperCase().replace(/[^A-Z]/g, "");
  if (cipherKeywordError(letters)) return [];
  return [...DIGIT_ORDER].map((digit, i) => ({ digit, letter: letters[i] }));
}

/**
 * Write a number in the shop's cipher, or "" if the keyword is unusable.
 *
 * Rounded to whole rupees: the floor is already a multiple of 5, and paisa on a
 * price tag would only make the code longer to read.
 */
export function encodeAmount(amount, keyword) {
  const letters = (keyword || "").toUpperCase().replace(/[^A-Z]/g, "");
  if (cipherKeywordError(letters)) return "";
  const n = Math.round(Number(amount) || 0);
  if (n <= 0) return "";
  return [...String(n)]
    .map((d) => letters[DIGIT_ORDER.indexOf(d)])
    .join("");
}

/** Read a code back, for checking a tag. Returns null if it does not decode. */
export function decodeCode(code, keyword) {
  const letters = (keyword || "").toUpperCase().replace(/[^A-Z]/g, "");
  if (cipherKeywordError(letters)) return null;
  const clean = (code || "").toUpperCase().replace(/[^A-Z]/g, "");
  if (!clean) return null;
  let out = "";
  for (const c of clean) {
    const i = letters.indexOf(c);
    if (i === -1) return null;
    out += DIGIT_ORDER[i];
  }
  return Number(out);
}
